/**
 * Distribution Chart Component
 *
 * Pie/donut chart for showing distribution data.
 * Uses Recharts which is already installed.
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const COLORS = [
  '#3B82F6', // blue-500
  '#22C55E', // green-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
];

export default function DistributionChart({
  data = {},
  title,
  height = 250,
  showLegend = true,
  showPercentage = true,
}) {
  // Convert object to array format for Recharts
  // { public: 0.5, private: 0.3 } -> [{ name: 'public', value: 0.5 }, ...]
  const chartData = Object.entries(data).map(([name, value]) => ({
    name: formatLabel(name),
    value: typeof value === 'number' ? value : 0,
  }));

  // Filter out zero values
  const filteredData = chartData.filter((d) => d.value > 0);

  if (filteredData.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400"
        style={{ height }}
      >
        No data available
      </div>
    );
  }

  // Custom label
  const renderCustomLabel = ({ name, percent }) => {
    if (percent < 0.05) return null; // Don't show labels for tiny slices
    return showPercentage ? `${(percent * 100).toFixed(0)}%` : name;
  };

  return (
    <div>
      {title && (
        <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={filteredData}
            cx="50%"
            cy="50%"
            innerRadius={height * 0.2}
            outerRadius={height * 0.35}
            paddingAngle={2}
            dataKey="value"
            label={renderCustomLabel}
            labelLine={false}
          >
            {filteredData.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) =>
              typeof value === 'number' && value <= 1
                ? `${(value * 100).toFixed(1)}%`
                : value
            }
          />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span className="text-sm text-gray-600">{value}</span>
              )}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Convert snake_case to Title Case
function formatLabel(str) {
  if (!str) return str;
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
